import { Icon, SkeletonLoader } from '@toggle/design-system';
import { formatTicker, TrackingStatus } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { getCardDate } from '~/utils/card-utils/card-utils';

import sharedStyles from '../../AlertsCard.module.scss';
import styles from './AlertsInsightCard.module.scss';

type AlertsInsightCardAlertEvent = {
  created_at?: string;
  source_id: string;
  title?: string;
  read?: boolean;
  tracking_status?: TrackingStatus;
  metadata?: {
    days_remaining?: number;
  };
  relation?: string;
};
interface AlertsInsightCardProps {
  alertEvent: AlertsInsightCardAlertEvent;
  setIsDropdownOpen?: (isDropdownOpen: boolean) => void;
  variant?: 'toast' | 'card';
  dropdown?: React.ReactNode;
}

export const AlertsInsightCard = ({
  alertEvent,
  dropdown,
  variant = 'card',
}: AlertsInsightCardProps) => {
  const { t } = useTranslation('alerts');
  const {
    created_at: createdAt,
    source_id: sourceId,
    title,
    read = false,
    tracking_status: trackingStatus,
    metadata,
    relation,
  } = alertEvent;
  const isToastVariant = variant === 'toast';

  const { data, isLoading } = useGetInsightWidgetData(sourceId);
  const entity = data?.data?.entity;

  const isTrackingActive = trackingStatus === 'active';
  const isTrackingCompleted = trackingStatus === 'completed';

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '60px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!entity) {
    return null;
  }

  return (
    <>
      <div className={sharedStyles.header}>
        <div className={sharedStyles.headerLeft}>
          <div className={styles.assetNameText}>{entity.name}</div>
          <div className={sharedStyles.dot} />
          <div className={styles.secondaryAssetInfoText}>
            {formatTicker(entity)}
          </div>
          {relation && (
            <>
              <div className={sharedStyles.dot} />
              <div className={styles.secondaryAssetInfoText}>{relation}</div>
            </>
          )}
        </div>
        {!isToastVariant && createdAt && (
          <div className={sharedStyles.headerRight}>
            <span className={sharedStyles.dateSpan}>
              {getCardDate(new Date(createdAt))}
            </span>
            {dropdown}
          </div>
        )}
      </div>
      <div className={sharedStyles.bodyWrapper}>
        <div className={sharedStyles.logoWrapper}>
          <AssetLogoContainer
            entity={entity}
            size={32}
            shouldUseBorderOnFallback
          />
          <div className={styles.logoBadge}>
            <Icon
              iconName="ExploreNav"
              size={12}
              fillColor="var(--icon-decorative-indigo)"
            />
          </div>
        </div>
        <div className={sharedStyles.textContent}>
          <p
            className={clsx(sharedStyles.primaryText, {
              [sharedStyles.read]: read,
              [sharedStyles.isToastVariant]: isToastVariant,
            })}
          >
            {title}
          </p>
          {!isToastVariant && (trackingStatus || metadata?.days_remaining) && (
            <div className={styles.secondaryText}>
              {trackingStatus && (
                <span
                  className={clsx(styles.trackingStatus, {
                    [styles.isTracking]: isTrackingActive,
                  })}
                >
                  •{' '}
                  {t(
                    `alerts:alfredInsightCard.trackingStatus.${trackingStatus}`
                  )}
                </span>
              )}
              {metadata?.days_remaining !== undefined && (
                <span className={styles.horizon}>
                  ·{' '}
                  {t(
                    `alerts:alfredInsightCard.${
                      isTrackingCompleted ? 'daysElapsed' : 'daysRemaining'
                    }`,
                    {
                      count: metadata.days_remaining,
                    }
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
